<template>
    <div>
        <!-- File Upload Section -->
        <v-row class="mb-4 ma-4">
            <v-col cols="12" md="6">
                <v-file-input
                    v-model="selectedFile"
                    label="Select Excel file"
                    accept=".xlsx,.xls"
                    show-size
                    prepend-icon="mdi-file-excel"
                />
            </v-col>
            <v-col cols="12" md="6" class="d-flex align-center">
                <v-btn
                    color="primary"
                    :disabled="!selectedFile"
                    @click="processExcelFile"
                    :loading="loading"
                >
                    <v-icon left>mdi-upload</v-icon>
                    Process & Save Excel
                </v-btn>
            </v-col>
        </v-row>

        <!-- Data Table -->
        <div class="ma-4">
            <v-data-table
                :headers="headers"
                :items="filteredTableData"
                :loading="loading"
                :mobile-breakpoint="0"
                disable-pagination
                hide-default-footer
                :height="'47vh'"
                fixed-header
            >
                <!-- File name display -->
                <template v-slot:top>
                    <div
                        class="pa-4"
                        :style="{
                            'background-color': '#eeeeee',
                        }"
                    >
                        <h2>
                            Dies
                        </h2>
                        <div class="text-caption grey--text">
                            {{ filteredTableData.length }} of
                            {{ originalData.length }} rows
                        </div>
                    </div>
                </template>
                <!-- Dynamic headers with text fields for filtering -->
                <template
                    v-for="(header, index) in headers"
                    v-slot:[`header.${header.value}`]
                >
                    <v-text-field
                        :key="`header-${index}`"
                        :label="header.text"
                        v-model="filters[header.value]"
                        hide-details
                        dense
                        clearable
                        class="mt-5"
                    />
                </template>

                <!-- Dynamic items -->
                <template
                    v-for="(header, index) in headers"
                    v-slot:[`item.${header.value}`]="{ item }"
                >
                    <span :key="`item-${index}`">{{ item[header.value] }}</span>
                </template>
            </v-data-table>

            <!-- No filtered results -->
            <v-alert
                v-if="
                    originalData.length > 0 &&
                        filteredTableData.length === 0 &&
                        !loading
                "
                type="warning"
                outlined
                class="mt-4"
            >
                <div class="text-center">
                    <v-icon size="48" class="mb-2">mdi-filter-remove</v-icon>
                    <p class="mb-0">
                        No results found matching your filters
                    </p>
                    <v-btn
                        small
                        color="primary"
                        class="mt-2"
                        @click="clearFilters"
                    >
                        Clear Filters
                    </v-btn>
                </div>
            </v-alert>
        </div>
    </div>
</template>

<script>
import * as XLSX from 'xlsx'
import { mapMutations } from 'vuex'
import API from '@/services/api'

export default {
    name: 'ExtrusionsSettings',
    data: () => ({
        selectedFile: null,
        loading: false,
        headers: [],
        originalData: [],
        filters: {},
        processedData: [],
        requiredColumns: [
            'matriz',
            'medida1',
            'medida2',
            'alloy',
            'temper',
            'espesor1',
        ],
    }),
    computed: {
        filteredTableData() {
            // Return empty array if no data
            if (!this.originalData || this.originalData.length === 0) {
                return []
            }

            // Check if any filters are active
            const hasActiveFilters = Object.keys(this.filters).some(
                key => this.filters[key] && this.filters[key].trim()
            )

            // Return all data if no filters active
            if (!hasActiveFilters) {
                return this.originalData
            }

            // Apply filters
            const filtered = this.originalData.filter(row => {
                return Object.keys(this.filters).every(key => {
                    const filterValue = this.filters[key]
                    if (!filterValue || !filterValue.trim()) return true

                    const cellValue = row[key]
                        ? row[key].toString().toLowerCase()
                        : ''
                    const matches = cellValue.includes(
                        filterValue.toLowerCase().trim()
                    )
                    return matches
                })
            })

            return filtered
        },
    },
    async mounted() {
        try {
            this.loading = true
            await this.loadExistingExtrusions()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),

        clearFilters() {
            // Clear all filters
            Object.keys(this.filters).forEach(key => {
                this.$set(this.filters, key, '')
            })
        },

        async processExcelFile() {
            if (!this.selectedFile) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: 'Please select an Excel file',
                })
                return
            }

            try {
                this.loading = true

                // Read the Excel file
                const workbook = XLSX.read(
                    await this.readFileAsArrayBuffer(this.selectedFile),
                    {
                        type: 'array',
                    }
                )

                // Get the first worksheet
                const sheetName = workbook.SheetNames[0]
                const worksheet = workbook.Sheets[sheetName]

                // Convert to JSON
                const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                    defval: '',
                })

                if (jsonData.length < 2) {
                    throw new Error(
                        'Excel file must have at least one header row and one data row'
                    )
                }

                // Process headers (first row) and validate required columns
                const headerRow = jsonData[0]
                const headerMap = {}

                // Create mapping of column names to indices
                headerRow.forEach((header, index) => {
                    const headerText = header
                        .toString()
                        .trim()
                        .toLowerCase()
                    headerMap[headerText] = index
                })

                // Validate required columns
                const missingColumns = this.requiredColumns.filter(
                    col => !Object.prototype.hasOwnProperty.call(headerMap, col)
                )

                if (missingColumns.length > 0) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: `Missing required columns: ${missingColumns.join(
                            ', '
                        )}. Required columns are: ${this.requiredColumns.join(
                            ', '
                        )}`,
                    })
                    return
                }

                // Reset data
                this.headers = []
                this.originalData = []
                this.filters = {}
                this.processedData = {}

                // Process headers for display
                this.headers = headerRow
                    .map((header, index) => {
                        const headerText = header.toString().trim()
                        const calculatedWidth = Math.max(
                            180,
                            headerText.length * 8
                        )

                        return {
                            text: headerText,
                            value: `col_${index}`,
                            align: 'left',
                            sortable: true,
                            filterable: true,
                            originalIndex: index,
                            width: calculatedWidth,
                        }
                    })
                    .filter(header => header.text && header.text.length > 0)

                // Process data rows as array
                this.processedData = []
                this.originalData = []

                jsonData.slice(1).forEach(row => {
                    // Create item for table display
                    const tableItem = {}
                    this.headers.forEach(header => {
                        tableItem[header.value] =
                            row[header.originalIndex] || ''
                    })
                    this.originalData.push(tableItem)

                    // Create array item with all column data
                    const extrusionItem = {}
                    headerRow.forEach((headerName, index) => {
                        const cleanHeaderName = headerName
                            .toString()
                            .trim()
                            .toLowerCase()

                        // Only process columns that have a valid header (not empty)
                        if (cleanHeaderName && cleanHeaderName.length > 0) {
                            const value = row[index]?.toString().trim() || ''
                            extrusionItem[cleanHeaderName] = value
                        }
                    })
                    this.processedData.push(extrusionItem)
                })

                // Initialize filters with Vue.set for reactivity
                this.filters = {}
                this.headers.forEach(header => {
                    this.$set(this.filters, header.value, '')
                })

                // Automatically save the processed data
                await this.saveExtrusions()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message || 'Error processing Excel file',
                })
            } finally {
                this.loading = false
            }
        },

        readFileAsArrayBuffer(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = e => resolve(e.target.result)
                reader.onerror = e => reject(e)
                reader.readAsArrayBuffer(file)
            })
        },

        getHeaderText(index) {
            if (this.headers && this.headers[index]) {
                return this.headers[index].text
            }
            return `Column ${index + 1}`
        },
        getProcessedData() {
            return this.processedData
        },
        getMatrizCount() {
            return Object.keys(this.processedData).length
        },
        async loadExistingExtrusions() {
            const existingExtrusions = await API.getExtrusions()
            if (
                existingExtrusions &&
                Object.keys(existingExtrusions).length > 0
            ) {
                // Convert object to array for display
                const extrusionData = Object.values(existingExtrusions)
                this.processedData = extrusionData

                // Create headers from the first row's keys for display
                const firstRow = extrusionData[0]
                this.headers = Object.keys(firstRow).map((key, index) => ({
                    text: key,
                    value: `col_${index}`,
                    align: 'left',
                    sortable: true,
                    filterable: true,
                    originalIndex: index,
                    width: Math.max(180, key.length * 8),
                }))

                // Use the extrusion data directly for table display
                this.originalData = extrusionData.map(row => {
                    const tableItem = {}
                    Object.keys(row).forEach((key, index) => {
                        tableItem[`col_${index}`] = row[key]
                    })
                    return tableItem
                })

                // Initialize filters
                this.filters = {}
                this.headers.forEach(header => {
                    this.$set(this.filters, header.value, '')
                })
            }
        },
        async saveExtrusions() {
            if (
                !Array.isArray(this.processedData) ||
                this.processedData.length === 0
            ) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: 'No extrusion data to save',
                })
                return
            }

            try {
                // Convert array to object using matriz as key
                const objectData = {}
                this.processedData.forEach(item => {
                    if (item.matriz) {
                        objectData[item.matriz] = item
                    }
                })

                await API.saveExtrusions(objectData)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message || 'Error saving extrusions',
                })
            }
        },
    },
}
</script>

<style scoped>
.v-data-table {
    border: 1px solid #e0e0e0;
}

.v-data-table-header th {
    background-color: #f5f5f5 !important;
}
</style>
